import { useState, FormEvent } from "react";
import { X, Mail, Loader2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useClickOutside } from '../../hooks/useClickOutside';
import useEscapeKey from "../../hooks/useEscapeKey";
import { motion } from "framer-motion";

interface AuthRequiredModalProps {
  onClose: (email?: string) => void;
}

export default function AuthRequiredModal({
  onClose,
}: AuthRequiredModalProps) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { login } = useAuth();

  const dialogRef = useClickOutside<HTMLDivElement>(onClose);
  useEscapeKey(onClose); // Use escape key to close the dialog

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login(email);
      setEmailSent(true);
      // Don't close the modal here - let the user see the email sent confirmation
    } catch (error) {
      console.error("Error sending magic link:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle close with email if email was sent
  const handleClose = () => {
    if (emailSent) {
      onClose(email);
    } else {
      onClose();
    }
  };

  return (
    <motion.div
      className="fixed inset-0 bg-gray-950/50 dark:bg-gray-950/70 flex items-center justify-center z-[200]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }} // Standard overlay transition
    >
      <motion.div
        ref={dialogRef}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[480px] mx-4 border border-gray-200 dark:border-gray-700"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
        {/* Header */}
        <div className="px-6 pt-5 rounded-t-lg">
          <div className="flex justify-between items-center">
            {!emailSent ? (
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Your Data, Your Rules
              </h2>
            ) : (
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Check Your Email!
              </h2>
            )}
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
            <div className="p-6">
              {!emailSent && (
                /* Banner Image */
                <div className="mb-3">
                  <img
                    src="/assets/login-banner.png"
                    alt="Login Banner"
                    className="w-full rounded-md"
                  />
                </div>
              )}
              {emailSent ? (
                <div className="text-center px-8">
                  <Mail className="mx-auto h-12 w-12 text-blue-600 dark:text-blue-500" />
                  <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
                    We've sent a magic link to <strong>{email}</strong>. Click the
                    link to sign in.
                  </p>
                  <div className="flex flex-col space-y-3 mt-6">
                    <button
                      onClick={() => setEmailSent(false)}
                      className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-500 hover:text-blue-500 dark:hover:text-blue-400"
                    >
                      Use a different email
                    </button>
                    <button
                      onClick={handleClose}
                      className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                  Data strategy starts here! Use your work email to organize projects, collaborate with your team, and build a secure and compliant data collection framework.
                  </p>
                  <form className="space-y-4" onSubmit={handleSubmit}>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Work Email
                      </label>
                      <div className="mt-1">
                        <input
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full px-3 pr-4 py-2 border border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
                          placeholder="you@company.com"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                    <div>
                      <button
                        type="submit"
                        disabled={isLoading || !email}
                        className={`
                      w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white
                      ${
                        isLoading || !email
                          ? "bg-gray-400 dark:bg-gray-600 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      }
                    `}
                      >
                        {isLoading ? (
                          <span className="flex items-center">
                            <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                            Sending...
                          </span>
                        ) : (
                          "Send Magic Link"
                        )}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
      </motion.div>
    </motion.div>
  );
}
